/**
 * One row of the notification centre.
 *
 * Renders either a single notification or a coalesced thread
 * ("NCR-0042 — 6 updates") that expands to the individual updates. Every
 * affordance the flat list had — select, open, mark read, delete — is kept;
 * the thread adds an expand toggle and marks its whole bucket read in one go.
 *
 * Deleting is offered per notification only. A collapsed thread never deletes
 * several rows behind one click; expand it and delete the ones you mean.
 */
import { useState } from 'react';

import type { Notification } from '../../lib/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Check, ChevronDown, ChevronRight, Clock, Layers, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { NotificationCentreEntry } from './groupNotifications';
import { getNotificationIcon } from './notificationIcons';
import { getSeverityMeta, resolveSeverity } from './severity';

export interface NotificationEntryRowProps {
  entry: NotificationCentreEntry;
  /** Ids currently ticked in the page-level selection. */
  selectedIds: Set<number>;
  /** Tick or untick every id in one go. */
  onSelectionChange: (ids: number[], selected: boolean) => void;
  onOpen: (notification: Notification) => void;
  onMarkRead: (ids: number[]) => void;
  onDelete: (id: number) => void;
}

/** A single update inside an expanded thread. */
function ThreadChildRow({
  notification,
  onOpen,
  onMarkRead,
  onDelete,
}: {
  notification: Notification;
  onOpen: (notification: Notification) => void;
  onMarkRead: (ids: number[]) => void;
  onDelete: (id: number) => void;
}) {
  const severity = resolveSeverity(notification);
  const severityMeta = getSeverityMeta(severity);

  return (
    <div
      className={cn(
        'flex items-start gap-3 py-2 pl-4 pr-2 rounded-md hover:bg-muted/50 transition-colors',
        !notification.is_read && 'bg-blue-50/50 dark:bg-blue-900/10'
      )}
    >
      <span className={cn('mt-1 h-2 w-2 shrink-0 rounded-full', !notification.is_read ? 'bg-blue-500' : 'bg-transparent')} />
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onOpen(notification)}>
        <p className={cn('text-sm', !notification.is_read && 'font-medium')}>{notification.title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{notification.message}</p>
        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {notification.time_ago}
          </span>
          <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0', severityMeta.badgeClass)}>
            {severityMeta.badgeLabel}
          </Badge>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {!notification.is_read && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => {
              e.stopPropagation();
              onMarkRead([notification.id]);
            }}
            title="Mark as read"
          >
            <Check className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(notification.id);
          }}
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function NotificationEntryRow({
  entry,
  selectedIds,
  onSelectionChange,
  onOpen,
  onMarkRead,
  onDelete,
}: NotificationEntryRowProps) {
  const [expanded, setExpanded] = useState(false);
  const severityMeta = getSeverityMeta(entry.severity);
  const Icon = getNotificationIcon(entry.iconType);
  const ids = entry.notifications.map((notification) => notification.id);
  const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id));
  const isThread = entry.kind === 'thread';
  const latest = entry.notifications[0];

  return (
    <div
      className={cn(
        'transition-colors',
        severityMeta.accentClass,
        !entry.isRead && 'bg-blue-50/50 dark:bg-blue-900/10'
      )}
    >
      <div className="flex items-start gap-4 p-4 hover:bg-muted/50 transition-colors">
        <Checkbox
          checked={allSelected}
          onCheckedChange={() => onSelectionChange(ids, !allSelected)}
          aria-label={`Select ${entry.headline}`}
        />

        <div className={cn('p-2 rounded-full shrink-0', severityMeta.chipClass)}>
          <Icon className="h-5 w-5" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div
              className="min-w-0 cursor-pointer"
              onClick={() => {
                if (isThread) {
                  setExpanded((prev) => !prev);
                } else if (latest) {
                  onOpen(latest);
                }
              }}
            >
              <p className={cn('font-medium', !entry.isRead && 'font-semibold')}>{entry.headline}</p>
              <p className="text-sm text-muted-foreground mt-1">{entry.message}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {entry.unreadCount > 0 && (
                <Badge variant="default" className="text-xs">
                  {isThread ? `${entry.unreadCount} new` : 'New'}
                </Badge>
              )}
              <Badge variant="outline" className={cn('text-xs', severityMeta.badgeClass)}>
                {severityMeta.badgeLabel}
              </Badge>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {entry.timeAgo}
            </span>
            {isThread ? (
              <button
                type="button"
                className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                onClick={() => setExpanded((prev) => !prev)}
                aria-expanded={expanded}
              >
                {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                <Layers className="h-3 w-3" />
                {expanded ? 'Hide updates' : `Show ${entry.itemCount} updates`}
              </button>
            ) : (
              entry.actionUrl && (
                <span className="text-blue-600 dark:text-blue-400">Click to view</span>
              )
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          {entry.unreadCount > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                onMarkRead(
                  entry.notifications.filter((n) => !n.is_read).map((n) => n.id)
                );
              }}
              title={isThread ? 'Mark all updates as read' : 'Mark as read'}
            >
              <Check className="h-4 w-4" />
            </Button>
          )}
          {!isThread && latest && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(latest.id);
              }}
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {isThread && expanded && (
        <div className="pl-16 pr-4 pb-3 space-y-1">
          {entry.notifications.map((notification) => (
            <ThreadChildRow
              key={notification.id}
              notification={notification}
              onOpen={onOpen}
              onMarkRead={onMarkRead}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
